import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BrIconComponent } from '../../shared/ui/icon/icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * The lasting offer to take a move back.
 *
 * Lifted from the Leads board so the roll-off board could carry the same
 * button without a second copy of its markup and its `rb-undo` styles. What
 * the button knows is how to look: disabled rather than hidden when there is
 * nothing to undo, so the safety net is visible BEFORE the mistake that needs
 * it; the count printed on it, so "is anything still waiting" is answered
 * without hovering; and colour only while armed, so "you can still undo" is
 * readable from across the page. What it does not know is what a move is or
 * how one is undone - the page says that in the tooltip and does it on `undo`.
 */
@Component({
  selector: 'app-undo-button',
  standalone: true,
  imports: [CommonModule, BrIconComponent, MatTooltipModule],
  template: `
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm ub"
            [class.ub--armed]="count > 0 && !busy"
            [disabled]="count === 0 || busy"
            [matTooltip]="tooltip"
            (click)="undo.emit()">
      <br-icon>undo</br-icon> Undo<span *ngIf="count"> ({{ count }})</span>
    </button>
  `,
  styles: [`
    /* Off the track's right end: it acts on moves, not on any one stage, so
       it must not read as another stop on the rail beside it. */
    :host { display: inline-flex; margin-left: auto; align-self: center; flex: 0 0 auto; }
    .ub br-icon { font-size: 16px; width: 16px; height: 16px; }
    .ub--armed {
      border-color: var(--br-color-primary);
      color: var(--br-color-primary);
      font-weight: 600;
    }
  `],
})
export class UndoButtonComponent {
  /** How many moves are waiting to be taken back. Zero disables the button. */
  @Input() count = 0;
  /** What pressing it will do - "Undo" alone invites a click to find out, and
   *  this button moves a record. */
  @Input() tooltip = 'Nothing to undo';
  /** True while a retraction is in flight: one at a time, in order. */
  @Input() busy = false;
  @Output() undo = new EventEmitter<void>();
}
